import React, { useState } from 'react';
import { BellIcon, ChevronDownIcon, FolderIcon, GraduationCapIcon, HashIcon, MicIcon, PaperclipIcon, PencilRulerIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Select } from '../ui/Field';
import { DrawingCanvas } from './DrawingCanvas';
import { VoiceRecorder } from './VoiceRecorder';
import { useNotes } from '../../contexts/NotesContext';
import { folderPath } from '../../utils/notes';
import { courses } from '../../data/academics';
import type { Note } from '../../types/notes';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-08-20' → '20 Aug 2026', matching the labels used elsewhere in Notes. */
function formatReminderDate(value: string): string {
  const [year, month, day] = value.split('-');
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`;
}
export function NoteMetaStrip({
  note,
  readOnly
}: {
  note: Note;
  readOnly: boolean;
}) {
  const {
    folders,
    moveNote,
    addTag,
    removeTag,
    addAttachment,
    removeAttachment,
    setReminder,
    updateNote
  } = useNotes();
  const [open, setOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const [drawOpen, setDrawOpen] = useState(false);
  const [reminderDate, setReminderDate] = useState('2026-08-20');
  const [reminderTime, setReminderTime] = useState('08:00');
  const summary = [folderPath(note.folderId, folders), note.tags.length > 0 ? `${note.tags.length} tag${note.tags.length === 1 ? '' : 's'}` : null, note.attachments.length > 0 ? `${note.attachments.length} attachment${note.attachments.length === 1 ? '' : 's'}` : null, note.reminder ? `reminder ${note.reminder.dateLabel}` : null, note.academicLinks.length > 0 ? `${note.academicLinks.length} link` : null].filter(Boolean).join(' · ');
  return <div className="border-b border-line bg-canvas" data-id="element-3759" data-mp="6garnctwpr39">
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className="flex w-full items-center gap-2 px-5 py-2 text-left sm:px-8" data-id="element-3760" data-mp="6hlwzh3036wh">
        <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 text-ink-subtle transition-transform duration-150 ease-out ${open ? '' : '-rotate-90'}`} data-id="element-3761" data-mp="gcxbbyahyc3p" />
        <span className="cz-num min-w-0 flex-1 truncate text-[12px] text-ink-muted" data-id="element-3762" data-mp="pedizy9xg048">{summary}</span>
        <span className="text-[12px] font-medium text-ink-muted" data-id="element-3763" data-mp="ucf40oqwy9ic">{open ? 'Hide' : 'Details'}</span>
      </button>

      {open && <div className="grid gap-4 border-t border-line px-5 py-4 sm:px-8 lg:grid-cols-2" data-id="element-3764" data-mp="143t9dd5jq3je">
          <div data-id="element-3765" data-mp="145ni7t3jm574">
            <p className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3766" data-mp="1935untgrr2jv">
              <FolderIcon className="h-3.5 w-3.5" data-id="element-3767" data-mp="1d5j00grlsyg4" /> Folder
            </p>
            <Select value={note.folderId ?? ''} disabled={readOnly} onChange={event => moveNote(note.id, event.target.value || null)} aria-label="Move to folder" className="h-9 text-[13px]" data-id="element-3768" data-mp="2xma42m36ijhm">
              <option value="" data-id="element-3769" data-mp="2onoliqxpj4gu">Unfiled</option>
              {folders.map(folder => <option key={folder.id} value={folder.id} data-id="element-3770" data-mp="2jov9z41zdnjb">
                  {folderPath(folder.id, folders)}
                </option>)}
            </Select>
          </div>

          <div data-id="element-3771" data-mp="3cc7u77ko5j9g">
            <p className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3772" data-mp="3cdsdg11j9fs0">
              <HashIcon className="h-3.5 w-3.5" data-id="element-3773" data-mp="32m4smizhewzi" /> Tags
            </p>
            <div className="flex flex-wrap items-center gap-1.5" data-id="element-3774" data-mp="32jsd0ch74ypm">
              {note.tags.map(tag => <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-line bg-surface px-2 py-0.5 text-[12px] font-medium text-ink-muted" data-id="element-3775" data-mp="3vaaej929us0x">
                  #{tag}
                  {!readOnly && <button type="button" aria-label={`Remove tag ${tag}`} onClick={() => removeTag(note.id, tag)} className="text-ink-subtle hover:text-danger-700" data-id="element-3776" data-mp="3jrrmw58dqx58">
                      <XIcon className="h-3 w-3" data-id="element-3777" data-mp="2dw8hjpm3954" />
                    </button>}
                </span>)}
              {!readOnly && <form onSubmit={event => {
            event.preventDefault();
            addTag(note.id, tagDraft);
            setTagDraft('');
          }} className="flex items-center gap-1" data-id="element-3778" data-mp="3otqrtjjrq24a">
                  <input value={tagDraft} onChange={event => setTagDraft(event.target.value)} placeholder="add tag" aria-label="Add a tag" className="h-7 w-24 rounded-full border border-line-strong bg-surface px-2.5 text-[12px] text-ink placeholder:text-ink-subtle focus:border-brand-700 focus:outline-none" data-id="element-3779" data-mp="q2ozr2vagste" />
                  <button type="submit" aria-label="Add tag" className="rounded-full p-1 text-ink-subtle hover:text-ink" data-id="element-3780" data-mp="2fl5dajefujgv">
                    <PlusIcon className="h-3.5 w-3.5" data-id="element-3781" data-mp="1mwqbiykbhxnh" />
                  </button>
                </form>}
            </div>
          </div>

          <div data-id="element-3782" data-mp="1vw3a2w6wl1tf">
            <p className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3783" data-mp="1vvli4br9x9rb">
              <GraduationCapIcon className="h-3.5 w-3.5" data-id="element-3784" data-mp="3c91vtttfrouq" /> Academic context
            </p>
            <div className="flex flex-wrap gap-1.5" data-id="element-3785" data-mp="37a8hjz6brtti">
              {note.academicLinks.map(link => <Badge key={link.label} tone="brand" data-id="element-3786" data-mp="24wstrlt0ivp7">
                  {link.kind}: {link.label}
                </Badge>)}
              {note.academicLinks.length === 0 && <p className="text-[12px] text-ink-muted" data-id="element-3787" data-mp="2dzlibhat8tbc">
                  Not linked to anything. This note stands on its own.
                </p>}
            </div>
            {!readOnly && <Select value="" onChange={event => {
          const course = courses.find(item => item.id === event.target.value);
          if (!course) return;
          updateNote(note.id, {
            academicLinks: [...note.academicLinks, {
              kind: 'Course',
              label: `${course.code} — ${course.title}`
            }]
          });
        }} aria-label="Link a course" className="mt-2 h-9 text-[13px]" data-id="element-3788" data-mp="1m8nq0eccvaqe">
                <option value="" data-id="element-3789" data-mp="zxw6h3xo8bpw">Link a course…</option>
                {courses.filter(course => course.status === 'registered').map(course => <option key={course.id} value={course.id} data-id="element-3790" data-mp="pzq5cojov9m3">
                      {course.code} — {course.title}
                    </option>)}
              </Select>}
            <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-3791" data-mp="h0cxg0b7qt3h">
              Linking never changes who owns this note or who can read it. Your lecturer does not gain
              access by being named here.
            </p>
          </div>

          <div data-id="element-3792" data-mp="7875gqkyoboa">
            <p className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3793" data-mp="2b6z0ek796wn">
              <BellIcon className="h-3.5 w-3.5" data-id="element-3794" data-mp="3uf96j04c8ibm" /> Reminder
            </p>
            {note.reminder ? <div className="flex flex-wrap items-center gap-2" data-id="element-3795" data-mp="3pgxuciqklvq8">
                <Badge tone="warn" data-id="element-3796" data-mp="1h7ga4k3chp2b">
                  {note.reminder.dateLabel} · {note.reminder.timeLabel}
                </Badge>
                {!readOnly && <Button size="sm" variant="ghost" onClick={() => setReminder(note.id, undefined)} data-id="element-3797" data-mp="1m5i2xwqkp2qz">
                    Remove
                  </Button>}
              </div> : readOnly ? <p className="text-[12px] text-ink-muted" data-id="element-3798" data-mp="1c9y7tcju283a">None set.</p> : <form onSubmit={event => {
          event.preventDefault();
          if (!reminderDate) return;
          setReminder(note.id, {
            dateLabel: formatReminderDate(reminderDate),
            timeLabel: reminderTime,
            sortKey: `${reminderDate}T${reminderTime}`
          });
        }} className="flex flex-wrap items-end gap-2" data-id="element-3799" data-mp="yem5ok3clfcc">
                <label className="flex flex-col gap-1" data-id="element-3800" data-mp="kgedsql65481">
                  <span className="text-[12px] text-ink-muted" data-id="element-3801" data-mp="um8v370yit5">Date</span>
                  <input type="date" value={reminderDate} min="2026-08-14" onChange={event => setReminderDate(event.target.value)} className="cz-num h-9 rounded-lg border border-line-strong bg-surface px-2.5 text-[13px] text-ink focus:border-brand-700 focus:outline-none" data-id="element-3802" data-mp="wyze4qum1ht" />
                </label>
                <label className="flex flex-col gap-1" data-id="element-3803" data-mp="ev6r9y912cm4">
                  <span className="text-[12px] text-ink-muted" data-id="element-3804" data-mp="eun8ygfgtm7k">Time</span>
                  <input type="time" value={reminderTime} onChange={event => setReminderTime(event.target.value)} className="cz-num h-9 rounded-lg border border-line-strong bg-surface px-2.5 text-[13px] text-ink focus:border-brand-700 focus:outline-none" data-id="element-3805" data-mp="tirv5udq8fv3" />
                </label>
                <Button type="submit" size="sm" variant="secondary" disabled={!reminderDate} data-id="element-3806" data-mp="3eytkl10i7iii">
                  Set reminder
                </Button>
              </form>}
            <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-3807" data-mp="3588nji0ham1p">
              Delivered through your existing Campuzon notification preferences.
            </p>
          </div>

          <div className="lg:col-span-2" data-id="element-3808" data-mp="307kms4mg6qua">
            <p className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3809" data-mp="1s1gr2mro8op">
              <PaperclipIcon className="h-3.5 w-3.5" data-id="element-3810" data-mp="3sym8koknbkms" /> Attachments
            </p>
            {note.attachments.length > 0 && <ul className="grid gap-2 sm:grid-cols-2" data-id="element-3811" data-mp="3o0sy3jcb84gp">
                {note.attachments.map(attachment => <li key={attachment.id} className="flex items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2.5" data-id="element-3812" data-mp="2cjkyrcgiteje">
                    {attachment.previewUrl ? <img src={attachment.previewUrl} alt={attachment.name} className="h-10 w-10 shrink-0 rounded object-cover" data-id="element-3813" data-mp="n40j5k97yckd" /> : <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-canvas text-ink-subtle" data-id="element-3814" data-mp="11rbqf9ydbaeo">
                        {attachment.kind === 'audio' ? <MicIcon className="h-4 w-4" data-id="element-3815" data-mp="16t16x54d0ga5" /> : <PaperclipIcon className="h-4 w-4" data-id="element-3816" data-mp="1aucw59jbmzht" />}
                      </span>}
                    <span className="min-w-0 flex-1" data-id="element-3817" data-mp="1kqzf1ss37gh7">
                      <span className="block truncate text-[13px] font-medium text-ink" data-id="element-3818" data-mp="1kod9tzso5d25">
                        {attachment.name}
                      </span>
                      <span className="cz-num block text-[12px] text-ink-muted" data-id="element-3819" data-mp="3mivq71aceobg">
                        {attachment.size} · added {attachment.addedOn}
                        {attachment.durationSeconds ? ` · ${Math.floor(attachment.durationSeconds / 60)}:${String(attachment.durationSeconds % 60).padStart(2, '0')}` : ''}
                      </span>
                      {attachment.transcript && <span className="mt-1 block rounded border border-line bg-canvas px-2 py-1 text-[12px] italic leading-5 text-ink-muted" data-id="element-3820" data-mp="191tl6sf5old">
                          “{attachment.transcript}”
                        </span>}
                      {attachment.transcriptionState === 'unsupported' && <Badge tone="warn" className="mt-1" data-id="element-3821" data-mp="63nugg0vjyr7">
                          No transcription on this device
                        </Badge>}
                      {attachment.transcriptionState === 'none' && <Badge tone="neutral" className="mt-1" data-id="element-3822" data-mp="zl178i6rqenr">
                          No speech recognised
                        </Badge>}
                      {attachment.strokeCount !== undefined && <Badge tone="neutral" className="mt-1" data-id="element-3823" data-mp="pmv5q1ovjajf">
                          {attachment.strokeCount} stroke{attachment.strokeCount === 1 ? '' : 's'}
                        </Badge>}
                    </span>
                    {!readOnly && <button type="button" aria-label={`Remove ${attachment.name}`} onClick={() => removeAttachment(note.id, attachment.id)} className="shrink-0 rounded p-1 text-ink-subtle hover:text-danger-700" data-id="element-3824" data-mp="kotolmb0dlal">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-3825" data-mp="3t5wqpqv0ache" />
                      </button>}
                  </li>)}
              </ul>}
            {!readOnly && <div className="mt-2 space-y-2" data-id="element-3826" data-mp="109d4pm2xx87j">
                <div className="flex flex-wrap items-start gap-2" data-id="element-3827" data-mp="10ao99o47mjzp">
                  <label className="inline-flex" data-id="element-3828" data-mp="vi3h0r0yceya">
                    <input type="file" className="sr-only" onChange={event => {
                const file = event.target.files?.[0];
                if (!file) return;
                const isImage = file.type.startsWith('image/');
                const size = `${Math.max(1, Math.round(file.size / 1024))} KB`;
                if (isImage) {
                  const reader = new FileReader();
                  reader.onload = () => addAttachment(note.id, {
                    kind: 'image',
                    name: file.name,
                    size,
                    addedOn: '14 Aug 2026',
                    previewUrl: String(reader.result)
                  });
                  reader.readAsDataURL(file);
                } else {
                  addAttachment(note.id, {
                    kind: 'document',
                    name: file.name,
                    size,
                    addedOn: '14 Aug 2026'
                  });
                }
                event.target.value = '';
              }} data-id="element-3829" data-mp="vgsf6wr2hh9t" />
                    <span className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-line-strong bg-surface px-3 text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:border-ink-subtle" data-id="element-3830" data-mp="20h0f83xu0jcd">
                      <PaperclipIcon className="h-3.5 w-3.5" data-id="element-3831" data-mp="3bw6tv0b5fdow" /> Attach a file or image
                    </span>
                  </label>

                  <Button size="sm" variant="secondary" icon={<PencilRulerIcon className="h-3.5 w-3.5" data-id="element-3833" data-mp="1bufnwvh4atof" />} onClick={() => setDrawOpen(true)} data-id="element-3832" data-mp="1gtjwpl04qpe8">
                    Draw or handwrite
                  </Button>
                </div>

                <VoiceRecorder onSave={({
            durationSeconds,
            transcript,
            supported
          }) => addAttachment(note.id, {
            kind: 'audio',
            name: `voice-note-${note.attachments.length + 1}.webm`,
            size: `${Math.max(20, durationSeconds * 18)} KB`,
            addedOn: '14 Aug 2026',
            durationSeconds,
            transcript: transcript || undefined,
            transcriptionState: !supported ? 'unsupported' : transcript ? 'done' : 'none'
          })} data-id="element-3834" data-mp="1lsdf9ru9x6l9" />
              </div>}
            <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-3835" data-mp="4zzs3ml5rbs0">
              Attachments are stored as separate objects against this note, never inlined into its
              text. Voice transcripts are indexed with the note body, so searching finds what you said.
            </p>

            <DrawingCanvas open={drawOpen} onClose={() => setDrawOpen(false)} onSave={(dataUrl, strokeCount) => addAttachment(note.id, {
          kind: 'drawing',
          name: `drawing-${note.attachments.length + 1}.png`,
          size: `${Math.max(4, Math.round(dataUrl.length / 1400))} KB`,
          addedOn: '14 Aug 2026',
          previewUrl: dataUrl,
          strokeCount
        })} data-id="element-3836" data-mp="j015g1im0bod" />
          </div>
        </div>}
    </div>;
}